import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { CustomViewColumn } from 'src/app/shared/components/odata-filter/models/custom-view-column';
import { SelectedFilter } from 'src/app/shared/components/odata-filter/models/selected-filter';
import { SortDirection } from '@angular/material/sort';
import { CustomView } from 'src/app/shared/components/odata-filter/models/custom-view';
import { Subscription } from 'rxjs';
import { AdminCredentialsService } from 'src/app/admin/admin-home/admin-credentials/admin-credentials.service';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { PersonProfileService } from 'src/app/people/profile/person-profile.service';
import { PersonCredentialApplicationReportFields } from './person-credential-application-report.fields';
import { PersonCredentialApplicationReportModel } from './person-credential-application-report.model';
import { PersonCredentialApplicationReportService } from './person-credential-application-report.service';
import { ViewContext } from '../odata-filter/models/view-context';
import { ExportRequest } from '../odata-filter/models/export-request';
import { ExportService } from '../../services/export.service';
import { CredentialApplicationDefinitionModel } from '../../models/credential/credential-application-definition.model';

@Component({
  selector: 'app-person-credential-application-report',
  templateUrl: './person-credential-application-report.component.html',
  styleUrls: ['./person-credential-application-report.component.scss'],
})
export class PersonCredentialApplicationReportComponent implements OnInit, OnDestroy {
  title: string = '';
  dataSource = new MatTableDataSource();
  appliedFilters: SelectedFilter[] = [];
  report: PersonCredentialApplicationReportModel[] = [];

  sortColumnName = PersonCredentialApplicationReportFields.firstName;

  orderDirection: SortDirection = 'asc';
  customViewColumns: CustomViewColumn[] = [];
  credentialApplication: CredentialApplicationDefinitionModel;

  subscriptions: Subscription[] = [];
  isDrawerOpen: boolean = false;
  viewContextId: string;
  viewContext: ViewContext;
  credentialId: string;
  reportConfigured: boolean = false;

  constructor(
    public filterService: PersonCredentialApplicationReportService,
    private adminCredentialsService: AdminCredentialsService,
    private profileService: PersonProfileService,
    private exportService: ExportService,
    private route: ActivatedRoute,
    private router: Router
  ) {}

  async ngOnInit(): Promise<void> {
    this.subscriptions.push(
      this.filterService.reportConfigured.subscribe(() => {
        this.reportConfigured = true;
        if (this.selectedView) {
          this.makeDynamicTable();
          this.filterService.applyView(this.selectedView);
        }
      }),
      this.filterService.filterResultValues.subscribe((report) => {
        if (!report) return;
        this.report = report;
        this.dataSource.data = report;
      }),

      this.filterService.appliedFilters.subscribe((appliedFilters) => {
        if (!appliedFilters) return;
        this.appliedFilters = appliedFilters;
      }),
      this.profileService.personCredentialScenarioDeleted.subscribe((personScenarioId) => {
        if (!personScenarioId) return;
        this.report = this.report.filter((x) => x.id != personScenarioId);
        this.dataSource.data = this.report;
      }),
      this.router.events.pipe(filter((e) => e instanceof NavigationEnd)).subscribe(() => {
        this.toggleDetailsSidebar();
      })
    );

    this.orderBy = [PersonCredentialApplicationReportFields.firstName];
    this.toggleDetailsSidebar();
    this.viewContext = this.route.snapshot.data['viewContext'];
    let applicationId = this.route.snapshot.params['applicationId'];
    this.credentialId = this.route.snapshot.params['credentialId'];
    this.viewContextId = applicationId || this.credentialId;
    let viewId = this.route.snapshot.queryParams['viewId'];
    if (this.viewContext == ViewContext.InProgressPersonCredentialApplications && applicationId) {
      this.credentialApplication = await this.adminCredentialsService.getCredentialApplication(
        applicationId
      );
      this.title = this.credentialApplication
        ? `${this.credentialApplication.credentialDefinitionName} / ${this.credentialApplication.typeText}`
        : 'Credential Applications';
    } else if (
      this.viewContext == ViewContext.PersonCredentialApplicationReport &&
      this.credentialId
    ) {
      this.subscriptions.push(
        this.adminCredentialsService.credential.subscribe((credential) => {
          if (credential === undefined) {
            this.adminCredentialsService.getCredential(this.credentialId);
            return;
          }

          this.title = credential
            ? `${credential.name} / All Credential Applications`
            : 'All Credential Applications';
        })
      );
    } else {
      this.title = 'All Credential Applications';
    }

    await this.filterService.setApplicationId(
      viewId,
      this.credentialApplication?.id,
      this.credentialId,
      this.viewContext
    );
  }

  get orderBy() {
    return this.filterService.orderBy;
  }
  set orderBy(value) {
    this.filterService.orderBy = value;
  }
  get displayedColumns() {
    let dColumns = this.customViewColumns
      .sort((n1, n2) => n1.rank - n2.rank)
      .map((x) => x.columnDefinition);
    if (
      dColumns.indexOf('contextMenu') == -1 &&
      this.viewContext == ViewContext.InProgressPersonCredentialApplications
    ) {
      dColumns.unshift('contextMenu');
    }
    return dColumns;
  }

  get selectedView(): CustomView {
    return this.filterService.selectedView;
  }

  get isFetchingData(): boolean {
    return this.filterService.isFetchingData;
  }

  get totalResultCount(): number {
    return this.filterService.totalResultCount;
  }

  onSortChange(event) {
    this.sortColumnName = event.active;

    this.orderDirection = event.direction;
    this.orderBy = [`${this.sortColumnName} ${this.orderDirection}`];
    this.filterService.applyFilter(this.appliedFilters);
  }

  selectView(view: CustomView) {
    if (view && this.reportConfigured) {
      this.makeDynamicTable();
      this.filterService.applyView(view);
    }
  }
  getDynamicScenarioColumnValue(id: string, element: PersonCredentialApplicationReportModel) {
    let scenario = element.scenarios.find((x) => x.scenarioDefinitionId == id);
    if (!scenario) return '---';
    return `${scenario.scenarioCompletion}%`;
  }
  _;

  private makeDynamicTable() {
    this.customViewColumns = [
      CustomViewColumn.createSystemColumn(
        PersonCredentialApplicationReportFields.credentialApplicationNumber,
        'Application Number',
        0
      ),
      CustomViewColumn.createSystemColumn(
        PersonCredentialApplicationReportFields.firstName,
        'First Name',
        1
      ),
      CustomViewColumn.createSystemColumn(
        PersonCredentialApplicationReportFields.lastName,
        'Last Name',
        2
      ),
    ];
    if (this.viewContext == ViewContext.PersonScenarioReport) {
      this.customViewColumns.push(
        CustomViewColumn.createSystemColumn(
          PersonCredentialApplicationReportFields.status,
          'Status',
          3
        )
      );
    }

    if (this.viewContext == ViewContext.PersonCredentialApplicationReport) {
      if (!this.credentialId) {
        this.customViewColumns.push(
          CustomViewColumn.createSystemColumn(
            PersonCredentialApplicationReportFields.credentialName,
            'Credential Name',
            this.customViewColumns.length
          )
        );
      }
      this.customViewColumns.push(
        CustomViewColumn.createSystemColumn(
          PersonCredentialApplicationReportFields.status,
          'Application Status',
          this.customViewColumns.length
        )
      );
    } else {
      this.credentialApplication?.scenarios
        .filter((x) => x.isActive)
        .forEach((scenario) => {
          let column = new CustomViewColumn();
          column.customFieldId = scenario.id;
          column.isCustomField = true;
          column.rank = this.customViewColumns.length;
          column.displayName = scenario.name + ' Scenario';
          if (!this.customViewColumns.find((x) => x.customFieldId == scenario.id)) {
            this.customViewColumns.push(column);
          }
        });
    }
    this.customViewColumns.push(
      CustomViewColumn.createSystemColumn(
        PersonCredentialApplicationReportFields.credentialApplicationType,
        'Application Type',
        this.customViewColumns.length
      )
    );
    this.customViewColumns.push(
      CustomViewColumn.createSystemColumn(
        PersonCredentialApplicationReportFields.credentialApplicationStartDate,
        'Application Start',
        this.customViewColumns.length
      )
    );
    this.customViewColumns.push(
      CustomViewColumn.createSystemColumn(
        PersonCredentialApplicationReportFields.credentialApplicationEndDate,
        'Application End',
        this.customViewColumns.length
      )
    );
    this.customViewColumns.push(
      CustomViewColumn.createSystemColumn(
        PersonCredentialApplicationReportFields.credentialApplicationUpdatedOn,
        'Last Updated',
        this.customViewColumns.length
      )
    );
  }
  openSideBar(obj: PersonCredentialApplicationReportModel) {
    this.router.navigate(['details', obj.personId, obj.id], { relativeTo: this.route });
  }
  closeSideBar() {
    this.router.navigate(['.'], { relativeTo: this.route });
  }
  backToCredentialing() {
    this.router.navigate(
      this.viewContext == ViewContext.InProgressPersonCredentialApplications ||
        this.viewContext == ViewContext.AllInProgressPersonCredentialApplications
        ? ['/credentialing/']
        : ['/reports/credentialing']
    );
  }
  export() {
    let exportRequest: ExportRequest = {
      oDataQuery: this.filterService.currentODataFilterQuery,
      columns: this.customViewColumns,
    };
    this.exportService.exportRequest(exportRequest, this.viewContext);
  }
  private toggleDetailsSidebar() {
    if (this.route.firstChild && this.route.firstChild.snapshot.url[0].path == 'details') {
      this.isDrawerOpen = true;
    } else {
      this.isDrawerOpen = false;
    }
  }
  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }
}
